import React, { useEffect, useRef } from 'react';
import { ArrowLeft, Clock } from 'lucide-react';
import { useMessages } from '@/shared/hooks/useMessages';
import { Conversation } from '@/shared/hooks/useConversations';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import oneSignalService from '@/services/OneSignalService';
import { useI18n } from '@/contexts/I18nContext';
import { getInitials } from '@/utils/avatarUtils';

interface ChatWindowProps {
  conversation: Conversation;
  onBack: () => void;
}

// ── Date separator helpers ────────────────────────────────────────────────────

function toDateKey(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA'); // YYYY-MM-DD
}

function formatSeparator(iso: string, t: (key: string) => string): string {
  const date = new Date(iso);
  const now  = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth()    === now.getMonth()    &&
    date.getDate()     === now.getDate();
  const isYesterday = (() => {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    return (
      date.getFullYear() === y.getFullYear() &&
      date.getMonth()    === y.getMonth()    &&
      date.getDate()     === y.getDate()
    );
  })();

  if (isToday)     return t('pages.messages.today');
  if (isYesterday) return t('pages.messages.yesterday');
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, onBack }) => {
  const { messages, isLoading, sendMessage, isSending, sendError } =
    useMessages(conversation.id);
  const { t } = useI18n();

  const bottomRef = useRef<HTMLDivElement>(null);

  // Tell OneSignalService which conversation is on screen so it can suppress
  // incoming push notifications for messages the user is already reading.
  useEffect(() => {
    oneSignalService.setActiveConversation(conversation.id);
    return () => oneSignalService.setActiveConversation(null);
  }, [conversation.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const subtitle = [
    conversation.other_user_belt_rank
      ? conversation.other_user_belt_rank.charAt(0).toUpperCase() +
        conversation.other_user_belt_rank.slice(1) + ' Belt'
      : null,
    conversation.other_user_academy,
  ]
    .filter(Boolean)
    .join(' · ');

  const initials = getInitials(conversation.other_user_name);

  return (
    <div className="flex flex-col w-full" style={{ height: '100dvh' }}>

      {/* ── Header ── */}
      <div
        className="flex items-center gap-3 px-4 border-b border-white/10 flex-shrink-0"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <button
          onClick={onBack}
          className="p-1 text-white/60 hover:text-white transition-colors flex-shrink-0"
          aria-label="Back to inbox"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <FramedAvatar
          src={conversation.other_user_picture ?? undefined}
          initials={initials}
          beltRank={conversation.other_user_belt_rank}
          username={conversation.other_user_username ?? undefined}
          frameSize={{ w: 44, h: 48 }}
          isManual
        />

        <div className="flex flex-col min-w-0">
          <span className="text-white font-semibold text-sm truncate leading-tight">
            {conversation.other_user_name}
          </span>
          {subtitle && (
            <span className="text-white/50 text-xs truncate leading-tight">
              {subtitle}
            </span>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
            <FramedAvatar
              src={conversation.other_user_picture ?? undefined}
              initials={initials}
              beltRank={conversation.other_user_belt_rank}
              username={conversation.other_user_username ?? undefined}
              frameSize={{ w: 80, h: 88 }}
              isManual
            />
            <div>
              <p className="text-white font-semibold text-base">
                {conversation.other_user_name}
              </p>
              {subtitle && (
                <p className="text-white/50 text-xs mt-0.5">{subtitle}</p>
              )}
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              {t('pages.messages.beginning_of_chat', { name: conversation.other_user_name.split(' ')[0] })}
            </p>
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{t('pages.messages.retention_inline')}</span>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => {
              const showSeparator =
                idx === 0 ||
                toDateKey(msg.created_at) !== toDateKey(messages[idx - 1].created_at);

              return (
                <React.Fragment key={msg.id}>
                  {showSeparator && (
                    <div className="flex items-center justify-center my-4">
                      <span className="bg-white/10 text-white/50 text-[11px] px-3 py-1 rounded-full">
                        {formatSeparator(msg.created_at, t)}
                      </span>
                    </div>
                  )}
                  <MessageBubble message={msg} />
                </React.Fragment>
              );
            })}
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* ── Input ── */}
      <div
        className="flex-shrink-0"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <MessageInput onSend={sendMessage} isSending={isSending} />
      </div>
    </div>
  );
};
